import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints, baseQuery, formatErrors } from "utils/api/endpoints";
import {
  FixedPhoneState,
  FixedPhonePlansResponse,
  CheckoutSummaryResponse,
  CheckoutCoupon,
  FixedPhoneAccountDetailsResponse,
  UpdateAddressRequest,
  PortinRequest,
  PortinResponse,
  ReviewsResponse,
  FixedPhoneDashboardResponse,
  UpdatePlansResponse,
} from "./types";

export const initialState: FixedPhoneState = {
  loading: false,
  error: null,
  portRequestStatus: null,
};

const slice = createSlice({
  name: "fixedPhone",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setPortRequestStatus(state, action: PayloadAction<string | null>) {
      state.portRequestStatus = action.payload;
    },
  },
});

// Create RTK Query API
export const api = createApi({
  reducerPath: "fixedPhoneApi",
  baseQuery,
  tagTypes: [
    "FixedPhonePlans",
    "PaymentMethods",
    "FixedPhoneAccountDetails",
    "FixedPhoneReviews",
    "FixedPhoneDashboard",
  ],
  endpoints: (builder) => ({
    getFixedPhonePlans: builder.query<{ data: FixedPhonePlansResponse }, any>({
      query: () => ({
        ...endpoints.getFixedPhonePlans,
      }),
      providesTags: ["FixedPhonePlans"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getFixedPhoneUpsellPlans: builder.query<{ data: any[] }, any>({
      query: () => ({
        ...endpoints.getFixedPhoneUpsellPlans,
      }),
      providesTags: ["FixedPhonePlans"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getUpdatePlans: builder.query<UpdatePlansResponse, { customerId: string }>({
      query: (params) => ({
        ...endpoints.getUpdatePlans,
        params,
      }),
      providesTags: ["FixedPhonePlans"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getFixedPhoneAccountDetails: builder.query<
      FixedPhoneAccountDetailsResponse,
      void
    >({
      query: () => ({
        ...endpoints.fixedPhoneAccountDetails,
      }),
      providesTags: ["FixedPhoneAccountDetails"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getCheckoutSummary: builder.mutation<
      { data: CheckoutSummaryResponse },
      { productId: string; variantId: string; planId: string }
    >({
      query: (params) => ({
        ...endpoints.fixedPhoneCheckoutSummary,
        body: params,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    applyCoupon: builder.mutation<
      { data: CheckoutSummaryResponse },
      {
        productId: string;
        variantId: string;
        planId: string;
        couponCode: string;
      }
    >({
      query: (params) => ({
        ...endpoints.fixedPhoneCheckoutSummary,
        body: params,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getPaymentMethods: builder.query<any, { customerId: string }>({
      query: (params) => ({
        ...endpoints.getPaymentMethods,
        params: params,
      }),
      providesTags: ["PaymentMethods"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    createSubscription: builder.mutation<any, any>({
      query: (params) => ({
        ...endpoints.fixedPhoneCreateSubscription,
        body: params,
      }),
      invalidatesTags: ["FixedPhonePlans"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    updateAddress: builder.mutation<{ data: any }, UpdateAddressRequest>({
      query: (params) => ({
        ...endpoints.fixedPhoneUpdateAddress,
        body: params,
      }),
      invalidatesTags: ["FixedPhoneAccountDetails"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    updateE911Address: builder.mutation<{ data: any }, UpdateAddressRequest>({
      query: (params) => ({
        ...endpoints.fixedPhoneUpdateE911Address,
        body: params,
      }),
      invalidatesTags: ["FixedPhoneAccountDetails"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    activateLine: builder.mutation<
      { data: any },
      { stripeSubscriptionId: string }
    >({
      query: (params) => ({
        ...endpoints.fixedPhoneActivateLine,
        body: params,
      }),
      invalidatesTags: ["FixedPhoneAccountDetails"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    portin: builder.mutation<PortinResponse, PortinRequest>({
      query: (params) => ({
        ...endpoints.fixedPhonePortin,
        body: params,
      }),
      // invalidatesTags: ["FixedPhoneAccountDetails"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    checkPortStatus: builder.mutation<
      any,
      { customerId: string; zip_code: string }
    >({
      query: (params) => ({
        ...endpoints.fixedPhonePortinCheck,
        body: params,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getFixedPhoneReviews: builder.query<
      ReviewsResponse,
      { productId: string; page: number; limit: number }
    >({
      query: (params) => ({
        ...endpoints.getFixedPhoneReviews,
        params: {
          productId: params.productId,
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: ["FixedPhoneReviews"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    createCustomerReview: builder.mutation<
      any,
      { customerSupabaseId: string; productSupabaseId: string }
    >({
      query: (body) => ({
        ...endpoints.customerReview,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getFixedPhoneDashboard: builder.query<
      FixedPhoneDashboardResponse,
      { customerId: string }
    >({
      query: (params) => ({
        ...endpoints.fixedPhoneDashboard,
        params,
      }),
      providesTags: ["FixedPhoneDashboard"],
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        return {
          status: response.status,
          message:
            response.data?.message ||
            "An error occurred while fetching fixed phone dashboard data",
        };
      },
    }),
    generateFixedPhoneBillPdf: builder.query<Blob, { customerId: string }>({
      query: (params) => ({
        ...endpoints.fixedPhoneGenerateBillPdf,
        params,
        responseHandler: async (response) => {
          if (response.ok) {
            return response.blob();
          }
          throw new Error("Failed to generate bill PDF");
        },
      }),
      providesTags: ["FixedPhoneDashboard"],
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        return {
          status: response.status,
          message:
            response.data?.message ||
            "An error occurred while generating bill PDF",
        };
      },
    }),
    reactivateOrderSummary: builder.query<any, void>({
      query: () => ({
        ...endpoints.reactivateOrderSummary,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    reactivateCheckout: builder.mutation<any, { paymentMethodId: string }>({
      query: (body) => ({
        ...endpoints.reactivateCheckout,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    homePhoneMakePaymentOrderSummary: builder.query<any, void>({
      query: () => ({
        ...endpoints.homePhoneMakePaymentOrderSummary,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    homePhoneMakePaymentCheckout: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.homePhoneMakePaymentCheckout,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    homePhoneUpdatePlanOrderSummary: builder.query<
      { data: CheckoutSummaryResponse },
      {
        customerId: string;
        newPlanId: string;
        billingZip: string;
      }
    >({
      query: (body) => ({
        ...endpoints.homePhoneUpdatePlanOrderSummary,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    homePhoneUpdatePlanCheckout: builder.mutation<
      any,
      {
        customerId: string;
        currentPlanId: string;
        newPlanId: string;
        paymentMethodId: string;
      }
    >({
      query: (body) => ({
        ...endpoints.homePhoneUpdatePlanCheckout,
        body,
      }),
      invalidatesTags: ["FixedPhoneDashboard"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
  }),
});

export const { actions: fixedPhoneActions, reducer: fixedPhoneReducer } = slice;

// Selectors
export const selectFixedPhone = (state: any) => state.fixedPhone;

export const useFixedPhoneSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: slice.actions,
    ...api,
  };
};

/**
 * Example Usage:
 *
 * // For Queries:
 * export function FixedPhoneComponent() {
 *   const { actions, useGetFixedPhonePlansQuery } = useFixedPhoneSlice();
 *   const { data, isLoading, error } = useGetFixedPhonePlansQuery();
 *
 *   useEffect(() => {
 *     if (data) {
 *       // Handle fixed phone plans data
 *     }
 *   }, [data]);
 * }
 *
 * // For Mutations:
 * export function CheckoutComponent() {
 *   const { useGetCheckoutSummaryMutation } = useFixedPhoneSlice();
 *   const [getCheckoutSummary, { data, isLoading, error }] = useGetCheckoutSummaryMutation();
 *
 *   const handleCheckout = async () => {
 *     try {
 *       const result = await getCheckoutSummary({ productId, variantId, planId });
 *       // Handle checkout summary data
 *     } catch (err) {
 *       console.error('Checkout error:', err);
 *     }
 *   };
 * }
 */
